import React from 'react';
import { Typography, Link, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import LaunchIcon from '@mui/icons-material/Launch'; // Using LaunchIcon as it's standard for "open in new"
import { isRunningInOutlook } from '@/taskpane/services/office';

// Define the gradient - adjust colors as needed to match your icon's gradient
const gradientTextStyles = {
  // background: 'linear-gradient(45deg, #2563EB 30%, #7C3AED 90%)', // Example: Blue to Purple
  background: '#3660F4',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textFillColor: 'transparent',
  display: 'inline', // Important for the gradient to apply correctly to the text flow
};

const TooltipCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  boxShadow: "0px 8px 24px 0px rgba(0, 0, 0, 0.25)",
}));

const SourceHeader = styled(Typography)(({ theme }) => ({
  ...gradientTextStyles, // Apply gradient
  fontWeight: 'bold', // Bolder to match image
  marginBottom: theme.spacing(0.5), // Reduced margin
  fontSize: '0.9rem', // Slightly smaller font
  fontFamily: 'Inter, sans-serif',
}));

const DocumentTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold', // Bolder to match image
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.75), // Reduced margin
  fontSize: '0.875rem', // Slightly smaller font
  lineHeight: 1.3,
}));

const MetadataText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.75rem', // Smaller font for metadata
}));

const DocumentLink = styled(Link)(({ theme }) => ({
  ...gradientTextStyles, // Apply gradient
  fontFamily: 'Inter, sans-serif',
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  fontSize: '13px', // Smaller font for link
  fontWeight: '500', // Bolder
  '& svg': { // Style the icon within the link
    ...gradientTextStyles, // Apply gradient to icon
    fontSize: '1rem', // Adjust icon size
  }
}));

const DividerLine = styled(Box)(({ theme }) => ({
    height: '1px',
    backgroundColor: theme.palette.divider,
    margin: theme.spacing(1, 0, 1, 0), // my: 1
}));

interface CitationTooltipContentProps {
  citationNumber: number;
  documentName: string;
  pageNumber: number;
  documentType?: string;
  documentUrl?: string;
  onDocumentClick?: (e: React.MouseEvent, url: string) => void;
  // folderId and sousDossierId are passed to onDocumentClick, not directly used for styling here
}

export function CitationTooltipContent({
  citationNumber,
  documentName,
  pageNumber,
  documentType,
  documentUrl,
  onDocumentClick,
}: CitationTooltipContentProps) {
  const isOutlook = isRunningInOutlook();
  const handleDocumentClick = (e: React.MouseEvent) => {
    // The actual folderId and sousDossierId would be captured by the closure
    // of the handleOpenPdfInNewTab function passed as onDocumentClick
    if (documentUrl && onDocumentClick) {
      onDocumentClick(e, documentUrl);
    }
  };

  return (
    <TooltipCard>
      <SourceHeader variant="body1">
        Source [{citationNumber}]&nbsp;: {/* Added non-breaking space for consistent spacing */}
      </SourceHeader>
      
      <DocumentTitle variant="h6">
        {documentName}
      </DocumentTitle>
      
      <Box sx={{ display: 'flex', gap: 1.5, marginBottom: 0}}> {/* Reduced gap */}
        <MetadataText variant="body2" sx={{ minWidth: '70px' }}>
            {"Page : " + pageNumber}
        </MetadataText>
        {documentType && (
          <MetadataText variant="body2">
            Type : {documentType}
          </MetadataText>
        )}
      </Box>
      
      {documentUrl && isOutlook && (
        <>
            <DividerLine sx={{ marginTop: 2 }} />

            <DocumentLink
            href={documentUrl} // href is good for semantics, onClick handles actual action
            onClick={handleDocumentClick}
            >
            <LaunchIcon /> {/* Using standard Launch icon */}
            Afficher le document
            </DocumentLink>
        </>
      )}
    </TooltipCard>
  );
}